import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, dateFmt } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Plus, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Invoices() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: invoices, isLoading } = useQuery({ queryKey: ["invoices"], queryFn: () => get("/finance/invoices") });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => get("/crm/clients") });

  const statuses = useMemo(() => [...new Set((invoices || []).map((i) => i.status).filter(Boolean))], [invoices]);
  const filtered = useMemo(() => {
    return (invoices || []).filter((inv) => {
      if (search && !`${inv.number} ${inv.client_name}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      return true;
    });
  }, [invoices, search, statusFilter]);

  const create = useMutation({
    mutationFn: (body) => post("/finance/invoices", { ...body, client_name: clients.find((c) => c.id === body.client_id)?.name || body.client_id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); toast.success("Invoice created"); },
  });

  return (
    <div>
      <PageHeader title="Invoices" subtitle="Billing across clients and projects.">
        <FormDialog title="New Invoice" testid="new-invoice-modal"
          trigger={<Button data-testid="new-invoice-btn"><Plus className="mr-1.5 h-4 w-4" />New Invoice</Button>}
          fields={[
            { name: "client_id", label: "Client", type: "select", required: true, options: clients.map((c) => ({ value: c.id, label: c.name })) },
            { name: "description", label: "Description", required: true, full: true },
            { name: "amount", label: "Amount", type: "number", required: true },
            { name: "status", label: "Status", type: "select", options: ["Draft", "Sent"] },
            { name: "due_date", label: "Due Date", type: "date" },
          ]}
          onSubmit={(v) => create.mutateAsync(v)} />
      </PageHeader>
      {!isLoading && invoices?.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <SearchInput testid="invoice-search" placeholder="Search invoice # or client" value={search} onChange={setSearch} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="invoice-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {isLoading ? <Skeleton className="h-64" /> : !invoices?.length ? (
        <EmptyState icon={FileText} title="No invoices yet" />
      ) : !filtered.length ? (
        <EmptyState icon={FileText} title="No invoices match" description="Adjust your search or filters." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Invoice #</th><th className="px-4 py-3 font-semibold">Client</th>
              <th className="px-4 py-3 text-right font-semibold">Amount</th><th className="px-4 py-3 text-right font-semibold">Balance</th>
              <th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold">Due</th></tr></thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} data-testid={`inv-row-${inv.id}`} onClick={() => navigate(`/finance/invoices/${inv.id}`)}
                  className={cn("cursor-pointer border-b border-border last:border-0 hover:bg-muted/50", inv.status === "Overdue" && "bg-red-50 dark:bg-red-500/5")}>
                  <td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                  <td className="px-4 py-3 font-medium">{inv.client_name}</td>
                  <td className="px-4 py-3 text-right">{money(inv.amount)}</td>
                  <td className={cn("px-4 py-3 text-right", inv.balance > 0 && "font-semibold")}>{money(inv.balance)}</td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{dateFmt(inv.due_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
